import * as Cesium from "cesium";
import Type from "../Types";
import { getUsernameAndUserIdFormat, showToast } from "../Utility";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

let st = null;

const LAYER_KEYS = ["lane_marker", "virtual_line", "curbstone", "road_edge", "turning_line", "lanelet"];

const toWindow = (scene, cartesian) => {
  const ST = Cesium.SceneTransforms;
  if (!ST) return null;

  const fn = ST.wgs84ToWindowCoordinates || ST.worldToWindowCoordinates;
  if (typeof fn !== "function") return null;

  try {
    return fn(scene, cartesian, new Cesium.Cartesian2());
  } catch {
    return null;
  }
};

const makeInitialState = () => ({
  enabled: false,
  viewer: null,
  originalCameraControls: null,
  onDisable: null,
  onApplied: null,
  camLocked: false,

  dataSources: [],
  userData: null,

  phase: "drawBox", // drawBox | drawLine

  draggingBox: false,
  dragStart: null,
  dragEnd: null,

  draggingLine: false,

  picked: [], // [{ entity, index, positionBefore }]
  pointHighlights: [],

  lineStart: null,
  lineEnd: null,
  lineEntity: null,

  handler: null,
  overlayEl: null,
});

const normalizeKey = (k) => {
  let key = String(k || "").toLowerCase();
  if (key === " ") key = "space";
  if (key === "escape") key = "esc";
  return key;
};

const isAllowedLayerName = (layerName) => {
  const s = String(layerName || "").toLowerCase();
  return LAYER_KEYS.some((k) => s.includes(k));
};

const lockCamera = () => {
  if (!st?.viewer || st.camLocked) return;
  disableCameraControls(st.viewer, st.originalCameraControls);
  st.camLocked = true;
};

const unlockCamera = () => {
  if (!st?.viewer || !st.camLocked) return;
  restoreCameraControls(st.viewer, st.originalCameraControls);
  st.camLocked = false;
};

const ensureOverlay = () => {
  if (st.overlayEl) return;
  const el = document.createElement("div");
  el.style.position = "absolute";
  el.style.pointerEvents = "none";
  el.style.border = "2px solid rgba(255,0,0,0.95)";
  el.style.background = "rgba(255,0,0,0.08)";
  el.style.display = "none";
  el.style.zIndex = "9999";
  document.body.appendChild(el);
  st.overlayEl = el;
};

const destroyOverlay = () => {
  try {
    if (st.overlayEl?.parentNode) st.overlayEl.parentNode.removeChild(st.overlayEl);
  } catch {}
  st.overlayEl = null;
};

const clampRect = (a, b) => {
  const x1 = Math.min(a.x, b.x);
  const y1 = Math.min(a.y, b.y);
  const x2 = Math.max(a.x, b.x);
  const y2 = Math.max(a.y, b.y);
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1, x2, y2 };
};

const drawOverlayRect = (r) => {
  ensureOverlay();
  if (!st.overlayEl) return;
  if (!r) {
    st.overlayEl.style.display = "none";
    return;
  }
  st.overlayEl.style.display = "block";
  st.overlayEl.style.left = `${r.x}px`;
  st.overlayEl.style.top = `${r.y}px`;
  st.overlayEl.style.width = `${r.width}px`;
  st.overlayEl.style.height = `${r.height}px`;
};

const getEntityPositions = (entity, time) => {
  if (!entity?.polyline?.positions) return null;
  const p = entity.polyline.positions;
  if (typeof p.getValue === "function") return p.getValue(time);
  return p;
};

const getEntityLayerName = (entity) => {
  const ds = entity?.entityCollection?.owner;
  return ds?.layerName;
};

const clearPointHighlights = () => {
  const v = st?.viewer;
  if (!v) return;
  (st.pointHighlights || []).forEach((e) => {
    try {
      v.entities.remove(e);
    } catch {}
  });
  st.pointHighlights = [];
};

const clearLine = () => {
  const v = st?.viewer;
  if (!v) return;
  if (st.lineEntity) {
    try {
      v.entities.remove(st.lineEntity);
    } catch {}
  }
  st.lineEntity = null;
  st.lineStart = null;
  st.lineEnd = null;
  st.draggingLine = false;
};

const clearBoxSelection = () => {
  st.picked = [];
  clearPointHighlights();
};

const resetToStep1 = () => {
  clearLine();
  clearBoxSelection();
  drawOverlayRect(null);
  st.phase = "drawBox";
  showToast("Draw selection box (red). ESC to exit.", Type.NOTIFICATION.INFO, { duration: 3000 });
};

const cleanup = () => {
  if (!st) st = makeInitialState();

  try {
    if (st.handler) st.handler.destroy();
  } catch {}
  st.handler = null;

  clearLine();
  clearBoxSelection();
  drawOverlayRect(null);
  destroyOverlay();
  unlockCamera();

  try {
    window.removeEventListener("keydown", onKeyDown);
  } catch {}

  st = makeInitialState();
};

const addPointHighlight = (pos) => {
  const v = st.viewer;
  return v.entities.add({
    position: pos,
    point: {
      pixelSize: 10,
      color: Cesium.Color.YELLOW.withAlpha(0.95),
      outlineColor: Cesium.Color.BLACK.withAlpha(0.7),
      outlineWidth: 2,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
    },
  });
};

const scanEndpointsInRect = (rect) => {
  const v = st.viewer;
  const time = v.clock.currentTime;
  const scene = v.scene;
  const results = [];

  (st.dataSources || []).forEach((ds) => {
    const layerName = ds?.layerName;
    if (!isAllowedLayerName(layerName)) return;

    const ents = ds?.entities?.values || [];
    ents.forEach((ent) => {
      const positions = getEntityPositions(ent, time);
      if (!positions || positions.length < 2) return;

      const a = positions[0];
      const b = positions[positions.length - 1];

      const wa = toWindow(scene, a);
      const wb = toWindow(scene, b);

      const inA = wa && wa.x >= rect.x && wa.x <= rect.x2 && wa.y >= rect.y && wa.y <= rect.y2;
      const inB = wb && wb.x >= rect.x && wb.x <= rect.x2 && wb.y >= rect.y && wb.y <= rect.y2;

      if (inA) results.push({ entity: ent, index: 0, positionBefore: a });
      if (inB) results.push({ entity: ent, index: positions.length - 1, positionBefore: b });
    });
  });

  return results;
};

const updateEntityEndpoint = (entity, index, newPos) => {
  const v = st.viewer;
  const time = v.clock.currentTime;

  const positions = getEntityPositions(entity, time);
  if (!positions || !positions.length) return;

  const next = positions.slice();
  next[index] = newPos;

  if (entity.polyline) entity.polyline.positions = next;
};

const projectPointToAlignLine = (p) => {
  const A = st.lineStart;
  const B = st.lineEnd;
  if (!A || !B) return p;

  const mid = Cesium.Cartesian3.midpoint(A, B, new Cesium.Cartesian3());
  const frame = Cesium.Transforms.eastNorthUpToFixedFrame(mid);
  const inv = Cesium.Matrix4.inverse(frame, new Cesium.Matrix4());

  const aL = Cesium.Matrix4.multiplyByPoint(inv, A, new Cesium.Cartesian3());
  const bL = Cesium.Matrix4.multiplyByPoint(inv, B, new Cesium.Cartesian3());
  const pL = Cesium.Matrix4.multiplyByPoint(inv, p, new Cesium.Cartesian3());

  const vx = bL.x - aL.x;
  const vy = bL.y - aL.y;
  const denom = vx * vx + vy * vy;
  if (denom < 1e-12) return p;

  const wx = pL.x - aL.x;
  const wy = pL.y - aL.y;
  const t = (wx * vx + wy * vy) / denom;

  const proj = new Cesium.Cartesian3(aL.x + t * vx, aL.y + t * vy, pL.z);
  const out = Cesium.Matrix4.multiplyByPoint(frame, proj, new Cesium.Cartesian3());
  return out;
};

const commitAlign = () => {
  if (!st.picked?.length) {
    showToast("No endpoint selected.", Type.NOTIFICATION.ERROR, { duration: 2000 });
    return;
  }
  if (!st.lineStart || !st.lineEnd) {
    showToast("Draw align line first.", Type.NOTIFICATION.ERROR, { duration: 2000 });
    return;
  }

  const d = Cesium.Cartesian3.distance(st.lineStart, st.lineEnd);
  if (!Number.isFinite(d) || d < 0.05) {
    showToast("Align line is too short.", Type.NOTIFICATION.ERROR, { duration: 2200 });
    return;
  }

  const changedDataSources = new Set();
  st.picked.forEach((it) => {
    const ln = getEntityLayerName(it.entity);
    if (!isAllowedLayerName(ln)) return;
    const newP = projectPointToAlignLine(it.positionBefore);
    updateEntityEndpoint(it.entity, it.index, newP);

    const modifier = getUsernameAndUserIdFormat(st.userData);
    if (modifier) {
      if (!it.entity.properties) {
        it.entity.properties = new Cesium.PropertyBag();
      }
      if (typeof it.entity.properties.addProperty === "function" && !it.entity.properties.modifier) {
        it.entity.properties.addProperty("modifier");
      }
      it.entity.properties.modifier = new Cesium.ConstantProperty(modifier);
    }

    const ds = it?.entity?.entityCollection?.owner;
    if (ds) changedDataSources.add(ds);
  });

  showToast("Aligned.", Type.NOTIFICATION.SUCCESS, { duration: 1400 });

  try {
    st.onApplied?.(Array.from(changedDataSources));
  } catch {}

  // After Enter: hide everything and restart from step 1
  resetToStep1();
};

const onKeyDown = (e) => {
  if (!st?.enabled) return;

  const k = normalizeKey(e.key);

  if (k === "enter") {
    if (st.phase === "drawLine" && st.lineStart && st.lineEnd) {
      e.preventDefault();
      commitAlign();
    }
    return;
  }

  if (k === "esc") {
    e.preventDefault();

    // Step back: line -> box -> exit
    if (st.lineEntity || st.lineStart || st.lineEnd || st.draggingLine) {
      clearLine();
      st.phase = st.picked?.length ? "drawLine" : "drawBox";
      if (st.picked?.length) {
        showToast("Line cleared. Drag to draw align line (green). Enter to apply.", Type.NOTIFICATION.INFO, { duration: 3000 });
      } else {
        showToast("Draw selection box (red). ESC to exit.", Type.NOTIFICATION.INFO, { duration: 3000 });
      }
      return;
    }

    if (st.picked?.length) {
      clearBoxSelection();
      st.phase = "drawBox";
      showToast("Selection cleared. Draw selection box (red). ESC to exit.", Type.NOTIFICATION.INFO, { duration: 3000 });
      return;
    }

    try {
      st.onDisable?.();
    } catch {}
    cleanup();
  }
};

const setupHandler = () => {
  const v = st.viewer;
  if (!v) return;

  st.handler = new Cesium.ScreenSpaceEventHandler(v.scene.canvas);

  const pickPointOnGlobe = (screenPos) => {
    const scene = v.scene;
    if (!screenPos || !scene) return null;

    try {
      if (scene.pickPositionSupported) {
        const pp = scene.pickPosition(screenPos);
        if (Cesium.defined(pp)) return pp;
      }
    } catch {}

    try {
      const ray = scene.camera.getPickRay(screenPos);
      if (ray) {
        const gp = scene.globe.pick(ray, scene);
        if (Cesium.defined(gp)) return gp;
      }
    } catch {}

    try {
      const ep = scene.camera.pickEllipsoid(screenPos, scene.globe.ellipsoid);
      if (Cesium.defined(ep)) return ep;
    } catch {}

    return null;
  };

  const updateLineEntity = () => {
    if (!st.lineStart || !st.lineEnd) return;
    if (!st.lineEntity) {
      st.lineEntity = v.entities.add({
        polyline: {
          positions: [st.lineStart, st.lineEnd],
          width: 3,
          material: Cesium.Color.LIME.withAlpha(0.95),
          clampToGround: false,
        },
      });
      return;
    }
    try {
      st.lineEntity.polyline.positions = [st.lineStart, st.lineEnd];
    } catch {}
  };

  // BOX: start drag
  const beginBox = (pos) => {
    st.draggingBox = true;
    st.dragStart = pos;
    st.dragEnd = pos;
    lockCamera();
    drawOverlayRect(clampRect(st.dragStart, st.dragEnd));
  };

  // BOX: update drag
  const updateBox = (pos) => {
    if (!st.draggingBox) return;
    st.dragEnd = pos;
    drawOverlayRect(clampRect(st.dragStart, st.dragEnd));
  };

  // BOX: end drag
  const endBox = () => {
    if (!st.draggingBox || !st.dragStart || !st.dragEnd) return;
    st.draggingBox = false;
    unlockCamera();

    const rect = clampRect(st.dragStart, st.dragEnd);
    drawOverlayRect(null);
    if (rect.width < 4 || rect.height < 4) return;

    if (st.phase !== "drawBox") return;

    clearBoxSelection();
    const hits = scanEndpointsInRect(rect).filter((h) => isAllowedLayerName(getEntityLayerName(h.entity)));

    if (!hits.length) {
      showToast("No endpoint found in box.", Type.NOTIFICATION.ERROR, { duration: 2200 });
      return;
    }

    st.picked = hits;
    hits.forEach((h) => {
      const ph = addPointHighlight(h.positionBefore);
      st.pointHighlights.push(ph);
    });

    st.phase = "drawLine";
    showToast("Drag to draw align line (green). Enter to apply. ESC clears line.", Type.NOTIFICATION.INFO, { duration: 4500 });
  };

  // LINE: start drag
  const beginLine = (pos) => {
    const p = pickPointOnGlobe(pos);
    if (!p) return;

    st.draggingLine = true;
    st.lineStart = p;
    st.lineEnd = p;
    lockCamera();
    updateLineEntity();
  };

  // LINE: update drag
  const updateLine = (pos) => {
    if (!st.draggingLine || !st.lineStart) return;
    const p = pickPointOnGlobe(pos);
    if (!p) return;
    st.lineEnd = p;
    updateLineEntity();
  };

  // LINE: end drag
  const endLine = (pos) => {
    if (!st.draggingLine) return;
    st.draggingLine = false;
    unlockCamera();

    const p = pickPointOnGlobe(pos);
    if (p) {
      st.lineEnd = p;
      updateLineEntity();
    }

    showToast("Line ready. Enter to apply. ESC to redraw line.", Type.NOTIFICATION.INFO, { duration: 2500 });
  };

  st.handler.setInputAction((e) => {
    if (!e?.position) return;

    if (st.phase === "drawLine") {
      beginLine(e.position);
      return;
    }

    beginBox(e.position);
  }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

  st.handler.setInputAction((e) => {
    const p = e?.endPosition;
    if (!p) return;

    if (st.phase === "drawLine") {
      updateLine(p);
      return;
    }

    updateBox(p);
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  st.handler.setInputAction((e) => {
    const p = e?.position;

    if (st.phase === "drawLine") {
      endLine(p);
      return;
    }

    endBox();
  }, Cesium.ScreenSpaceEventType.LEFT_UP);
};

// Public API
export function toggleAlignEndpointsTool(viewer, disable, dataSources, originalCameraControls, onDisable, onApplied, userData) {
  if (!viewer) return;

  if (disable) {
    if (!st?.enabled) return;
    cleanup();
    showToast("Align disabled.", Type.NOTIFICATION.INFO, { duration: 1200 });
    return;
  }

  cleanup();

  st.enabled = true;
  st.viewer = viewer;
  st.dataSources = Array.isArray(dataSources) ? dataSources.filter(Boolean) : [];
  st.originalCameraControls = originalCameraControls || null;
  st.camLocked = false;
  st.onDisable = typeof onDisable === "function" ? onDisable : null;
  st.onApplied = typeof onApplied === "function" ? onApplied : null;
  st.userData = userData || null;
  if (!st.dataSources.length) {
    showToast("Align needs dataSources.", Type.NOTIFICATION.ERROR, { duration: 2500 });
    cleanup();
    return;
  }

  setupHandler();
  window.addEventListener("keydown", onKeyDown);

  showToast("Align enabled. Draw selection box (red). ESC to exit.", Type.NOTIFICATION.INFO, { duration: 3500 });
}
